// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

export interface App {
  id: number
  track_id: number
  bundle_id: string
  name: string
  developer: string
  country: string
  primary_locale: string
  icon_url: string
  is_mine: boolean
  asc_app_id: string
  source: string
  metadata_json: string
  rating: number
  rating_count: number
  price: number
  currency: string
  category: string
  description: string
  monthly_revenue: number
  monthly_downloads: number
  has_iap: boolean
  created_at: string
  updated_at: string
}

export interface Analysis {
  app_id: number
  market_score: number
  competition_score: number
  entry_risk_score: number
  overall_score: number
  market_report: string
  competition_report: string
  entry_risk_report: string
  recommendation: string
  created_at: string
  updated_at: string
}

export interface CompetitorLink {
  id: number
  app_id: number
  competitor_app_id: number
  note: string
  created_at: string
}

export interface CompetitorRow {
  link: CompetitorLink
  app: App
  /** Monthly US net revenue estimate from rating growth (internal/revest). */
  estimate?: RevenueEstimate
}

/** Revenue per download level: low $0.11 · mid $0.38 · high $0.94 · hard
 *  paywall $3.09 · paid = the app's price. */
export type RevenueTier = 'low' | 'mid' | 'high' | 'hard' | 'paid'

export interface RevenueEstimate {
  /** ok, or why there are no numbers: under 30 days of rating counts, the
   *  count did not rise, or a genre that earns outside App Store billing. */
  status: 'ok' | 'no_history' | 'no_growth' | 'non_iap'
  tier: RevenueTier
  tier_auto: boolean
  revenue_per_download: number
  from_date?: string
  to_date?: string
  days: number
  new_ratings: number
  ratings_per_month: number
  /** USD a month at 50 / 100 / 200 downloads per rating. */
  low: number
  mid: number
  high: number
  /** US top-grossing chart positions on the latest chart day; 0 = not in the top 100. */
  grossing?: { date: string; genre: string; genre_rank: number; overall_rank: number }
  /** Our apps only: US proceeds over the same days, per month. */
  actual?: { monthly: number; proceeds: number; from_date: string; to_date: string; days: number }
}

export interface RevenueModel {
  downloads_per_rating: number
  downloads_per_rating_low: number
  downloads_per_rating_high: number
  net_share: number
  window_days: number
  min_days: number
  tiers: Record<string, number>
}

export interface CompetitorList {
  items: CompetitorRow[]
  self?: { app: App; estimate: RevenueEstimate }
  model?: RevenueModel
}

export interface KeywordInsight {
  term: string
  country: string
  results_count: number
  popularity: number
  difficulty: number
  rank: number
  avg_rating: number
  avg_reviews: number
  large_ratio: number
}

export interface SuggestionResponse {
  sources: Record<string, string[]>
  errors: Record<string, string>
}

// Opportunity tiers — must mirror suggest.OpportunityCategory on the Go side.
export type Opportunity =
  | 'golden'
  | 'hidden_gem'
  | 'competitive'
  | 'low_value'
  | 'unscored'

// ResearchResult is the standalone /api/research payload: a KeywordInsight
// plus the top competing apps and a derived opportunity tier.
export interface ResearchResult extends KeywordInsight {
  top: ITunesResult[]
  opportunity: Opportunity
  demand?: DemandScore
  hints?: string[]
  /** What the App Store itself answers. Absent when it could not be read,
   *  and store_error then says why. */
  store?: StoreAnswer
  store_error?: string
}

export interface StoreApp {
  track_id: number
  name: string
  subtitle?: string
  rating_count: number
}

export interface StoreAnswer {
  results_count: number
  name_holders: number
  median_ratings: number
  top: StoreApp[]
}

export interface NameCheckInput {
  name: string
  subtitle: string
  keywords: string
  country: string
}

export interface NameCheckPhrase {
  phrase: string
  where: 'name_start' | 'name' | 'subtitle'
  demand: DemandScore
  store?: StoreAnswer
  store_error?: string
}

export interface NameCheckResult {
  country: string
  name_length: number
  subtitle_length: number
  keywords_length: number
  name_limit: number
  subtitle_limit: number
  keywords_limit: number
  duplicates: string[]
  phrases: NameCheckPhrase[]
}

/** One store of one change of a name or subtitle, at its 7- or 14-day check. */
export interface ListingEffect {
  app_id: number
  app: string
  icon: string
  locale: string
  country: string
  date: string
  days: number
  old_name: string
  new_name: string
  old_subtitle: string
  new_subtitle: string
  better: number
  worse: number
  same: number
  unknown: number
  /** A rank of 0 means not in the results. */
  moves: { term: string; before: number; after: number }[]
  /** Impressions from App Store search; null until Apple's files reach the end of the window. */
  search: { before: number; after: number; others_before: number; others_after: number } | null
}

export interface FunnelSources {
  from: string
  to: string
  sources: { source_type: string; impressions: number; page_views: number; first_downloads: number }[]
  countries: { country: string; search_impressions: number; search_downloads: number; downloads: number }[]
  days: { date: string; search_downloads: number; downloads: number }[]
}

export interface ListingRow {
  app_id: number
  locale: string
  captured_date: string
  name: string
  subtitle: string
  keywords: string
}

export * from './reportTypes'
import type { DailyReport, InsightReport, ReportListItem, RevenueSourceStatus } from './reportTypes'

// JobRun + JobSummary types now live in admin-design/_common/jobs (canonical
// wire types shared with saas-common/jobruns on the Go side). Re-exported
// for callers that still import from this module.
export type { JobRun, JobSummary } from '@/admin-design/_common/jobs'
import type { JobRunsResponse } from '@/admin-design/_common/jobs/types'

export interface DashboardStats {
  apps_count: number
  keywords_count: number
  top10_count: number
  top50_count: number
  unranked_count: number
  avg_overall: number
  analysis_count: number
  portfolio: {
    app: App
    overall_score: number
    best_rank: number
    best_term: string
    best_country: string
    keyword_count: number
    top_10: number
  }[]
  /** Gaps: searched, weak at the top, and we are 11th to 100th in store order. */
  opportunities: {
    keyword_id: number
    term: string
    country: string
    popularity: number
    demand_source: DemandScore['source']
    difficulty: number
    rank: number
    name_holders: number
    median_ratings: number
    top_count: number
    app: App
  }[]
  movers: {
    kind: string
    term: string
    country: string
    rank_before?: number
    rank_after?: number
    delta?: number
    at: string
    app: App
  }[]
}

export interface AnalysisRow {
  app: App
  analysis: Analysis
  keyword_count: number
}

export interface Keyword {
  id: number
  app_id: number
  term: string
  country: string
  locale: string
  tag: string
  source: string
  is_tracking: boolean
  notes: string
  /** Unix seconds the term left the listing; 0 while it is in it. */
  retired_at?: number
  created_at: string
  updated_at: string
}

/** How much a term is searched: Apple's own figure, or autocomplete below it. */
export interface DemandScore {
  popularity: number
  source: 'apple' | 'autocomplete' | 'none'
  genre?: string
  rank_in_genre?: number
  week?: string
  hint_position: number
  hint_checked: boolean
}

export interface KeywordSnapshot {
  id: number
  keyword_id: number
  snapshot_date: string
  rank: number
  popularity: number
  difficulty: number
  results_count: number
  top_apps_json: string
  source: string
  created_at: string
}

export interface RankPoint {
  date: string
  rank: number
}

export interface KeywordRow extends Keyword {
  latest?: KeywordSnapshot
  /** The rank in the store's own order, for the priority terms. Absent means
   *  not read; rank 0 means the app is not in the results. */
  store?: { rank: number; date: string; results_count: number }
  demand?: DemandScore
  history?: RankPoint[]
}

export interface ITunesResult {
  trackId: number
  trackName: string
  bundleId: string
  sellerName: string
  primaryGenreName: string
  price: number
  currency: string
  version: string
  averageUserRating: number
  userRatingCount: number
  artworkUrl100: string
  artworkUrl512?: string
}

export interface TrackingOverviewRow {
  app: App
  keyword_count: number
  top_10: number
  top_50: number
  ranked_50_plus: number
  unranked: number
  best_keyword?: {
    id: number
    term: string
    country: string
    rank: number
  }
  last_snapshot: string
  avg_popularity: number
  avg_difficulty: number
  analysis_score: number
}

export interface TrackingStatus {
  last_at: string
  last_ok: boolean
  last_msg: string
  running: boolean
  interval: number
  top_n: number
}

export interface TrackedKeyword {
  id: number
  term: string
  country: string
  app_id: number
  app_name: string
}

export interface CompetitionApp {
  track_id: number
  name: string
  current_rank: number // 0 = dropped out of the latest top-10
  prev_rank: number
  delta: number // positive = climbed
  status: 'up' | 'down' | 'same' | 'new' | 'out'
  series: number[] // aligned to CompetitionBoard.dates; 0 = absent that day
}

export interface CompetitionBoard {
  dates: string[] // oldest → newest
  apps: CompetitionApp[]
}

export interface AppDelta {
  track_id: number
  name: string
  rank: number
  from?: number
  to?: number
}

export interface AppChanges {
  entered_count: number
  left_count: number
  entered: AppDelta[]
  left: AppDelta[]
  moved: AppDelta[]
}

export interface Mover {
  keyword_id: number
  term: string
  country: string
  app_id: number
  app_name: string
  app_icon: string
  current_rank: number
  past_rank: number
  delta: number
  status: 'up' | 'down' | 'same' | 'new' | 'lost'
  series: number[]
  app_changes: AppChanges
}

export interface SummaryRow {
  app_id?: number
  app_name?: string
  app_icon?: string
  improved: number
  declined: number
  unchanged: number
  total: number
}

export type AnalysisPeriod = 'daily' | 'weekly' | 'monthly'

export interface AnalysisResult {
  period: AnalysisPeriod
  period_days: number
  summary: SummaryRow
  apps: SummaryRow[]
  movers: Mover[]
  /** Movers that passed the filter, before limit/offset cut the page.
   *  Without options it is movers.length. */
  total: number
}

/** Narrowing and paging for keywordAnalysis. Every field is optional; with
 *  none the request is the old one. `summary` and `apps` in the answer stay
 *  whole-portfolio whatever is set here. */
export interface KeywordAnalysisOptions {
  appId?: number
  status?: Mover['status'][]
  /** Part of the term, any case. */
  q?: string
  /** Only keywords ranked now. */
  ranked?: boolean
  /** 0 or absent = every mover. */
  limit?: number
  offset?: number
}

function keywordAnalysisPath(period: AnalysisPeriod, opts: KeywordAnalysisOptions): string {
  const p = new URLSearchParams({ period })
  if (opts.appId) p.set('app_id', String(opts.appId))
  if (opts.status && opts.status.length > 0) p.set('status', opts.status.join(','))
  const q = opts.q?.trim()
  if (q) p.set('q', q)
  if (opts.ranked) p.set('ranked', '1')
  if (opts.limit && opts.limit > 0) p.set('limit', String(opts.limit))
  if (opts.offset && opts.offset > 0) p.set('offset', String(opts.offset))
  return `/api/analysis/keywords?${p.toString()}`
}

export interface TrackedKeywordList {
  items: TrackedKeyword[]
  /** Keywords that matched q, before limit. */
  total: number
}

/** Every tracked keyword, or with `q` the ones whose term contains it (any
 *  case), at most `limit` of them.
 *
 *  Two signatures on purpose: the old call sites pass this method straight to
 *  react-query as `queryFn`, which calls it with its context object. The
 *  zero-argument form keeps that assignable, and the type guards make the
 *  context object mean "no filter" at run time. */
function listTrackedKeywords(): Promise<TrackedKeywordList>
function listTrackedKeywords(q: string, limit?: number): Promise<TrackedKeywordList>
function listTrackedKeywords(q?: unknown, limit?: unknown): Promise<TrackedKeywordList> {
  const p = new URLSearchParams()
  if (typeof q === 'string' && q.trim()) p.set('q', q.trim())
  if (typeof limit === 'number' && limit > 0) p.set('limit', String(Math.floor(limit)))
  const s = p.toString()
  return request<TrackedKeywordList>(s ? `/api/keywords/tracking?${s}` : '/api/keywords/tracking')
}

export interface ChangeEvent {
  id: number
  kind: string
  app_id?: number
  keyword_id?: number
  before_json: string
  after_json: string
  severity: string
  created_at: string
}


// ─── Play Console slot stock ──────────────────────────────────────────────
// Times are unix seconds.

export type PlaySlotStatus = 'created' | 'linked' | 'submitted' | 'live' | 'abandoned'

export interface PlaySlot {
  id: number
  package_name: string
  app_name: string
  status: PlaySlotStatus
  created_at: number
  linked_slug: string | null
  linked_at: number | null
  submitted_at: number | null
  notes: string
  updated_at: number
}

export interface PlaySlotAttempt {
  id: number
  at: number
  package_name: string
  kind: 'create' | 'dry_run' | 'verify'
  result: 'ok' | 'limit' | 'login_required' | 'error' | 'unverified'
  message: string
  duration_ms: number
}

export interface PlaySlotsSummary {
  /** Daily schedule only; a manual run works while it is off. */
  enabled: boolean
  per_day: number
  /** Cap on creates inside the last 7 days, hand-made ones adopted included. */
  per_week: number
  created_today: number
  created_7d: number
  unused: number
  last_attempt: PlaySlotAttempt | null
  next_allowed_at: number | null
  blocked_reason: string
}

export interface PlaySlotsResponse {
  summary: PlaySlotsSummary
  slots: PlaySlot[]
  attempts: PlaySlotAttempt[]
}

export interface PlaySlotsRunReport {
  dry_run: boolean
  trigger: string
  planned: number
  attempts: PlaySlotAttempt[]
  stopped_reason?: string
  blocked?: string
}

// ─── 확인할 것, cleared by hand ──────────────────────────────────────────
//
// The list is built here in the browser (lib/attention.ts); the server only
// keeps which occurrence of each item the operator cleared.

export interface AttentionDismissal {
  key: string
  /** The occurrence cleared; the item shows again once its stamp changes. */
  stamp: string
  dismissed_at: number
}

// ─── Web services' usage (Workers Analytics Engine) ──────────────────────
//
// Read from Cloudflare when asked and kept five minutes on the server; nothing
// is stored here. Buckets are UTC days, or hours on the 24-hour window, the
// same shape as the app tab's.

export interface WebService {
  id: string
  name: string
  dataset: string
  url: string
  enabled: boolean
}

export interface WebUsageTotals {
  sessions: number
  views: number
  time_ms: number
  /** epoch ms; 0 when nothing came in */
  last_event_at: number
  daily: { day: string; sessions: number }[]
}

export interface WebUsageSummary extends WebService, WebUsageTotals {
  /** Set when this one service could not be read. */
  error?: string
}

export interface WebUsageList {
  /** false until a Cloudflare token is in settings */
  configured: boolean
  items: WebUsageSummary[]
}

export interface WebUsageReport {
  configured: boolean
  service: WebService
  days: number
  internal: boolean
  totals: WebUsageTotals
  sources: { surface: string; source: string; sessions: number; time_ms: number }[]
  screens: { surface: string; screen: string; views: number; time_ms: number; sessions: number }[]
  controls: {
    surface: string
    screen: string
    control: string
    presses: number
    sessions: number
    /** sessions that pressed it / sessions that saw the screen */
    use_rate: number
    by_plan: Record<string, number>
    /** presses per device: desktop | tablet | phone */
    by_device: Record<string, number>
  }[]
  /** the heatmap's plan columns, in order; "" is pages without sign-in */
  plans: string[]
  pages: {
    path: string
    screen: string
    views: number
    time_ms: number
    sessions: number
    avg_depth: number
    done: number
    next: number
  }[]
  places: { country: string; device: string; sessions: number }[]
}

/** A service's screen map (internal/webusage/screenmap.go): its own pictures
 *  of its screens and where each named control sits, in CSS pixels. */
export interface WebUsageMap {
  /** false when the service's site has none */
  available: boolean
  captured?: string
  shots: {
    surface: string
    screen: string
    /** what was opened before the picture; "" is the screen as it loads */
    state: string
    label: string
    device: 'desktop' | 'phone'
    /** absolute URL on the service's site */
    image: string
    w: number
    h: number
    boxes: { c: string; x: number; y: number; w: number; h: number }[]
  }[]
}

// ─── First-party analytics (GA4 replacement) ──────────────────────────────
//
// `app_id` here is the BUNDLE id the app itself reports, not the ASO row id.
// `linked` is the ASO app matched on that bundle id — the join that lets one
// screen answer "rank improved, did conversion follow?".

export interface AnalyticsLinkedApp {
  id: number
  track_id: number
  name: string
  icon_url: string
  country: string
}

export interface AnalyticsAppSummary {
  app_id: string
  platforms: string
  linked?: AnalyticsLinkedApp
  sessions: number
  users: number
  screen_enters: number
  avg_session_ms: number
  paywall_shown: number
  purchase_success: number
  /** unpaired screen_enter / all screen_enter. A rise means the app broke. */
  unpaired_enter_rate: number
  last_event_at: number
  /** zero-filled one entry per UTC day in the window, oldest first */
  daily: AnalyticsAppDay[]
}

export interface AnalyticsAppDay {
  day: string
  sessions: number
  users: number
}

export interface AnalyticsUsageScreen {
  screen: string
  app_version: string
  users: number
  views: number
  views_per_user: number
  avg_dwell_ms: number
}

export interface AnalyticsUsageCountry {
  country: string
  users: number
  views: number
  views_per_user: number
  avg_session_ms: number
  screens: AnalyticsUsageScreen[]
}

export interface AnalyticsUsageApp {
  app_id: string
  linked?: { id: number; name: string; icon_url: string } | null
  versions: string
  users: number
  views: number
  views_per_user: number
  avg_session_ms: number
  countries: AnalyticsUsageCountry[]
}

export interface AnalyticsBreakdown {
  apps: AnalyticsUsageApp[]
  users: number
  views: number
  views_per_user: number
  avg_session_ms: number
}

export interface AnalyticsFunnelHour {
  /** "00".."23", UTC. */
  hour: string
  sessions: number
  users: number
  paywall_shown: number
  purchase_started: number
  purchase_success: number
}

export interface AnalyticsScreenRow {
  screen: string
  enters: number
  paired: number
  counted: number
  unpaired_enters: number
  orphan_exits: number
  discarded_neg: number
  discarded_long: number
  /** sheet transitions that passed through without anyone seeing the screen */
  discarded_short: number
  /** enters minus the transitions — the count to show a reader */
  views: number
  dwell_ms_total: number
  dwell_ms_max: number
  /** averaged over COUNTED spans only — unpaired enters contribute nothing */
  avg_dwell_ms: number
  sessions: number
  users: number
  unpaired_enter_rate: number
}

export interface AnalyticsFunnel {
  reason: string
  sessions: number
  users: number
  paywall_shown: number
  paywall_users: number
  purchase_started: number
  purchase_success: number
  purchase_cancelled: number
  purchase_failed: number
  paywall_rate: number
  start_rate: number
  conversion_rate: number
}

export interface AnalyticsFunnelDay {
  day: string
  sessions: number
  users: number
  paywall_shown: number
  purchase_started: number
  purchase_success: number
}

export interface AnalyticsRetentionPoint {
  day_offset: number
  cohort_size: number
  retained: number
  rate: number
}

export interface AnalyticsCountryRow {
  country: string
  sessions: number
  users: number
  screen_enters: number
  avg_session_ms: number
}

export interface AnalyticsDeviceRow {
  os: string
  os_version: string
  model: string
  sessions: number
  users: number
}

export interface AnalyticsLocaleRow {
  locale: string
  sessions: number
  users: number
}

export interface AnalyticsQualityRow {
  app_id: string
  platform: string
  app_version: string
  events: number
  enters: number
  exits: number
  paired_enters: number
  unpaired_enters: number
  orphan_exits: number
  discarded_neg: number
  discarded_long: number
  duplicate_events: number
  sessions: number
  users: number
  unpaired_enter_rate: number
  pair_rate: number
  duplicate_rate: number
  discarded_total: number
}

export interface AnalyticsExitScreen {
  screen: string
  sessions: number
  share: number
}

export interface AnalyticsAppReport {
  platform?: string
  platforms: string[]
  app_id: string
  days: number
  from: string
  to: string
  linked?: AnalyticsLinkedApp
  totals: {
    sessions: number
    users: number
    new_users: number
    events: number
    screen_enters: number
    avg_session_ms: number
    total_dwell_ms: number
    last_event_at: number
  }
  /** 24 UTC hour buckets. Present only for the one-day window. */
  funnel_hourly?: AnalyticsFunnelHour[]
  screens: AnalyticsScreenRow[]
  funnel: AnalyticsFunnel
  funnel_by_reason: AnalyticsFunnel[]
  funnel_daily: AnalyticsFunnelDay[]
  retention: AnalyticsRetentionPoint[]
  countries: AnalyticsCountryRow[]
  devices: AnalyticsDeviceRow[]
  locales: AnalyticsLocaleRow[]
  quality: AnalyticsQualityRow[]
  exit_screens: AnalyticsExitScreen[]
}

export interface AnalyticsStatus {
  cursor: string
  last_pull_at: number
  last_ok: boolean
  last_error: string
  pulled_total: number
  dup_total: number
  running: boolean
  configured: boolean
  last_run_at?: string
  last_run_ok?: boolean
  last_run_msg?: string
}

export interface AnalyticsPullSummary {
  pages: number
  fetched: number
  inserted: number
  duplicates: number
  rejected: number
  days: string[]
  sessions: number
  cursor: string
  aggregated: boolean
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),
  dashboard: () => request<DashboardStats>('/api/dashboard'),
  // Job-runs API moved to admin-design/_common/jobs (consumed directly by
  // JobsPage). The path also moved from /api/jobs/runs to /api/admin/jobs/runs.
  // jobRuns reads one job's newest runs, for the status the live screens show.
  jobRuns: (job: string, limit = 5) =>
    request<JobRunsResponse>(`/api/admin/jobs/runs?job=${encodeURIComponent(job)}&limit=${limit}`),

  // Apps
  listApps: () => request<{ items: App[] }>('/api/apps'),
  createApp: (body: Partial<App>) =>
    request<App>('/api/apps', { method: 'POST', body: JSON.stringify(body) }),
  getApp: (id: number) => request<App>(`/api/apps/${id}`),
  deleteApp: (id: number) => request<void>(`/api/apps/${id}`, { method: 'DELETE' }),
  importItunes: (track_id: number, country: string) =>
    request<App>('/api/apps/import/itunes', {
      method: 'POST',
      body: JSON.stringify({ track_id, country }),
    }),
  importASC: () => request<{ imported: App[]; asc_count: number }>('/api/apps/import/asc', { method: 'POST' }),

  // Keywords
  listKeywords: (appId: number) =>
    request<{ items: KeywordRow[] }>(`/api/apps/${appId}/keywords`),
  createKeyword: (appId: number, body: Partial<Keyword>) =>
    request<Keyword>(`/api/apps/${appId}/keywords`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteKeyword: (id: number) =>
    request<void>(`/api/keywords/${id}`, { method: 'DELETE' }),
  keywordHistory: (id: number, limit = 60) =>
    request<{ items: KeywordSnapshot[] }>(`/api/keywords/${id}/history?limit=${limit}`),

  // Tracking
  trackingStatus: () => request<TrackingStatus>('/api/tracking/status'),
  trackingRun: () => request<{ queued: boolean }>('/api/tracking/run', { method: 'POST' }),
  trackingOverview: () =>
    request<{ items: TrackingOverviewRow[] }>('/api/tracking/overview'),

  // Search / hints
  searchItunes: (term: string, country = 'us') =>
    request<{ items: ITunesResult[] }>(
      `/api/search/itunes?term=${encodeURIComponent(term)}&country=${country}`,
    ),
  searchHints: (prefix: string, country = 'us') =>
    request<{ items: { term: string }[] }>(
      `/api/search/hints?prefix=${encodeURIComponent(prefix)}&country=${country}`,
    ),

  // Standalone keyword research — evaluate any term ad-hoc (no app needed).
  research: (term: string, country = 'us') =>
    request<ResearchResult>(
      `/api/research?term=${encodeURIComponent(term)}&country=${country}`,
    ),

  nameCheck: (input: NameCheckInput) =>
    request<NameCheckResult>('/api/research/name-check', { method: 'POST', body: JSON.stringify(input) }),
  funnelSources: (appId: number, days = 28) =>
    request<FunnelSources>(`/api/funnel/sources?app_id=${appId}&days=${days}`),
  listingHistory: (appId: number) => request<{ items: ListingRow[] }>(`/api/apps/${appId}/listing-history`),

  // Changes
  changes: (limit = 50) => request<{ items: ChangeEvent[] }>(`/api/changes?limit=${limit}`),
  listingEffects: () => request<{ items: ListingEffect[] }>('/api/listing-effects'),

  // Keyword competition (top-10 rank movement)
  listTrackedKeywords,
  keywordCompetition: (id: number, limit = 14) =>
    request<CompetitionBoard>(`/api/keywords/${id}/competition?limit=${limit}`),

  // Whole-portfolio keyword rank-movement analysis (daily/weekly/monthly)
  keywordAnalysis: (period: AnalysisPeriod = 'weekly', opts: KeywordAnalysisOptions = {}) =>
    request<AnalysisResult>(keywordAnalysisPath(period, opts)),

  // Competitors
  listCompetitors: (appId: number) =>
    request<CompetitorList>(`/api/apps/${appId}/competitors`),
  /** The operator's revenue-per-download pick for one app; '' = automatic. */
  setRevenueTier: (appId: number, tier: RevenueTier | '') =>
    request<{ ok: boolean; tier: string }>(`/api/apps/${appId}/revenue-tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier }),
    }),
  addCompetitor: (appId: number, body: { track_id?: number; bundle_id?: string; country?: string; note?: string }) =>
    request<{ link: CompetitorLink; app: App }>(`/api/apps/${appId}/competitors`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  findCompetitors: (appId: number) =>
    request<{ candidates: ITunesResult[] }>(`/api/apps/${appId}/competitors/find`, { method: 'POST' }),
  bulkAddCompetitors: (appId: number, trackIds: number[], country = 'us') =>
    request<{ added: App[]; skipped: number[] }>(`/api/apps/${appId}/competitors/bulk`, {
      method: 'POST',
      body: JSON.stringify({ track_ids: trackIds, country }),
    }),
  deleteCompetitor: (id: number) =>
    request<void>(`/api/competitors/${id}`, { method: 'DELETE' }),

  // Keyword analyze / suggest
  analyzeKeyword: (appId: number, kid: number) =>
    request<KeywordInsight>(`/api/apps/${appId}/keywords/${kid}/analyze`, { method: 'POST' }),
  analyzeAllKeywords: (appId: number) =>
    request<{ items: KeywordInsight[] }>(`/api/apps/${appId}/keywords/analyze`, { method: 'POST' }),
  syncASCKeywords: (appId: number) =>
    request<{ imported: number; locales: number }>(`/api/apps/${appId}/keywords/sync_asc`, { method: 'POST' }),
  suggestKeywords: (appId: number, body?: { sources?: string[]; country?: string; seed?: string; limit?: number }) =>
    request<SuggestionResponse>(`/api/apps/${appId}/keywords/suggest`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  // Analysis
  getAnalysis: (appId: number) => request<Analysis>(`/api/apps/${appId}/analysis`),
  recomputeAnalysis: (appId: number) =>
    request<Analysis>(`/api/apps/${appId}/analysis/recompute`, { method: 'POST' }),
  listAnalyses: (includeCompetitors = false) =>
    request<{ items: AnalysisRow[] }>(
      `/api/analyses${includeCompetitors ? '?include=all' : ''}`,
    ),

  // SensorTower enrichment
  enrichSensorTower: (appId: number) =>
    request<{ monthly_revenue: number; monthly_downloads: number }>(`/api/apps/${appId}/enrich/sensortower`, { method: 'POST' }),
  // First-party analytics
  // `platform` scopes the numbers, not just which apps are listed. An app_id is
  // one string per PRODUCT so iOS and Android join into one funnel, which means
  // every figure is a cross-platform sum until the server is told otherwise.
  // `platforms` is the window's whole vocabulary, computed WITHOUT the filter:
  // each item's own `platforms` is scoped, so the picker cannot be built from
  // the rows without erasing every option but the chosen one.
  analyticsApps: (days = 30, platform = '') =>
    request<{ items: AnalyticsAppSummary[]; platforms: string[] }>(
      `/api/analytics/apps?days=${days}${platform ? `&platform=${platform}` : ''}`,
    ),
  // `at` is the rolling window's answer to `hour`: that window straddles two
  // UTC days, so an hour-of-day number cannot say which one is meant. It
  // carries the whole instant instead.
  analyticsApp: (
    appId: string,
    days = 30,
    platform = '',
    hour: number | null = null,
    day = '',
    at = '',
  ) =>
    request<AnalyticsAppReport>(
      `/api/analytics/apps/${encodeURIComponent(appId)}?days=${days}` +
        (platform ? `&platform=${platform}` : '') +
        (day ? `&day=${day}` : '') +
        (hour !== null ? `&hour=${hour}` : '') +
        (at ? `&at=${encodeURIComponent(at)}` : ''),
    ),
  // The GA4-shaped screen table: app x country groups, screen x version rows.
  analyticsBreakdown: (days = 30, platform = '') =>
    request<AnalyticsBreakdown>(
      `/api/analytics/breakdown?days=${days}${platform ? `&platform=${platform}` : ''}`,
    ),
  // Points a telemetry app_id at an ASO app row. `asoAppId` 0 clears it, and
  // the app falls back to matching on bundle id.
  analyticsLinkApp: (appId: string, asoAppId: number) =>
    request<{ app_id: string; aso_app_id?: number }>(
      `/api/analytics/apps/${encodeURIComponent(appId)}/link`,
      { method: 'PUT', body: JSON.stringify({ aso_app_id: asoAppId }) },
    ),
  analyticsQuality: (days = 14, appId?: string) =>
    request<{ items: AnalyticsQualityRow[] }>(
      `/api/analytics/quality?days=${days}${appId ? `&app_id=${encodeURIComponent(appId)}` : ''}`,
    ),
  playSlots: () => request<PlaySlotsResponse>('/api/playslots'),
  playSlotsRun: (dryRun: boolean) =>
    request<PlaySlotsRunReport>('/api/playslots/run', {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun }),
    }),
  playSlotsVerify: () => request<PlaySlotsRunReport>('/api/playslots/verify', { method: 'POST' }),
  playSlotsAdopt: (body: { package_name: string; app_name: string; created_at?: number }) =>
    request<PlaySlot>('/api/playslots/adopt', { method: 'POST', body: JSON.stringify(body) }),
  playSlotsPatch: (
    id: number,
    patch: { status?: PlaySlotStatus; linked_slug?: string; notes?: string },
  ) =>
    request<PlaySlot>(`/api/playslots/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  analyticsStatus: () => request<AnalyticsStatus>('/api/analytics/status'),
  analyticsPull: () =>
    request<AnalyticsPullSummary>('/api/analytics/pull', { method: 'POST' }),
  analyticsBackfill: (days = 7) =>
    request<{ days: string[]; sessions: number; spans: number; events: number }>(
      `/api/analytics/backfill?days=${days}`,
      { method: 'POST' },
    ),
  attentionDismissals: () => request<{ items: AttentionDismissal[] }>('/api/attention/dismissals'),
  dismissAttention: (key: string, stamp: string) =>
    request<AttentionDismissal>('/api/attention/dismissals', { method: 'POST', body: JSON.stringify({ key, stamp }) }),
  webUsageList: (days: number, internal: boolean) =>
    request<WebUsageList>(`/api/webusage?days=${days}${internal ? '&internal=1' : ''}`),
  webUsageReport: (id: string, days: number, internal: boolean) =>
    request<WebUsageReport>(`/api/webusage/${encodeURIComponent(id)}?days=${days}${internal ? '&internal=1' : ''}`),
  webUsageMap: (id: string) => request<WebUsageMap>(`/api/webusage/${encodeURIComponent(id)}/map`),

  // Auto reports and the revenue they are built from
  reports: () => request<ReportListItem[]>('/api/reports'),
  report: (date: string) => request<DailyReport>(`/api/reports/${date}`),
  buildReport: (date: string) => request<DailyReport>(`/api/reports/${date}/build`, { method: 'POST' }),
  insight: (from: string, to: string) =>
    request<InsightReport>(`/api/insight?from=${from}&to=${to}`),
  revenueStatus: () => request<RevenueSourceStatus[]>('/api/revenue/status'),
  collectRevenue: () => request<unknown>('/api/revenue/collect', { method: 'POST' }),
}
